import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import PortalLayout from "../components/PortalLayout";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import {
  Upload, Loader2, Building2, Heart, Trophy, GraduationCap,
  HeartHandshake, Briefcase, Droplets, Lock, Sparkles, Check,
  TrafficCone,
} from "lucide-react";

const INDUSTRY_ICONS = {
  building: Building2, heart: Heart, trophy: Trophy,
  "graduation-cap": GraduationCap, "heart-handshake": HeartHandshake,
  briefcase: Briefcase, droplets: Droplets, "traffic-cone": TrafficCone,
};

const AuditUpload = () => {
  const [industries, setIndustries] = useState([]);
  const [industry, setIndustry] = useState(() =>
    (typeof window !== "undefined" && localStorage.getItem("auditiq_industry")) || "");
  const [templates, setTemplates] = useState([]);
  const [templateKey, setTemplateKey] = useState("");
  const [orgName, setOrgName] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [detection, setDetection] = useState(null);
  const [detecting, setDetecting] = useState(false);
  const [perms, setPerms] = useState(null);
  const [demoDatasets, setDemoDatasets] = useState([]);
  const [demoKey, setDemoKey] = useState("");
  const detectAbort = useRef(null);  // eslint-disable-line no-unused-vars
  const nav = useNavigate();

  const isSalesDemo = perms?.role === "sales_demo";
  const demoMode = !!perms?.demo_mode;
  const showDemoDropdown = isSalesDemo || demoMode;

  useEffect(() => {
    api.get("/auditiq/industries").then(r => {
      setIndustries(r.data?.industries || []);
    }).catch(() => {});
    api.get("/auditiq/my-permissions").then(r => setPerms(r.data)).catch(() => {});
    api.get("/auditiq/demo-datasets").then(r => setDemoDatasets(r.data?.datasets || [])).catch(() => {});
  }, []);

  // Persist industry choice
  const selectIndustry = (key) => {
    setIndustry(key);
    if (typeof window !== "undefined") localStorage.setItem("auditiq_industry", key);
  };

  // Load templates whenever industry changes
  useEffect(() => {
    if (!industry) { setTemplates([]); return; }
    api.get(`/auditiq/templates?org_type=${encodeURIComponent(industry)}`).then(r => {
      const list = r.data?.templates || [];
      setTemplates(list);
      if (list.length && !list.find(t => t.key === templateKey)) {
        setTemplateKey(list[0].key);
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [industry]);

  const onDemoDatasetChange = (key) => {
    setDemoKey(key);
    if (!key) return;
    const ds = demoDatasets.find(d => d.key === key);
    if (ds) {
      if (ds.industry) selectIndustry(ds.industry);
      if (ds.template) setTemplateKey(ds.template);
    }
  };

  const onFileChosen = async (f) => {
    setFile(f);
    setDetection(null);
    if (!f || !f.name.toLowerCase().endsWith(".csv")) return;
    setDetecting(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/auditiq/detect-template", fd, {
        headers: { "Content-Type": "multipart/form-data" }, timeout: 30000,
      });
      setDetection(r.data);
    } catch {
      // silent
    }
    setDetecting(false);
  };

  const useRecommended = () => {
    if (!detection?.detected) return;
    setTemplateKey(detection.detected);
    toast.success(`Template set: ${detection.display_name}`);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!industry) return toast.error("Select an industry first");
    if (!templateKey) return toast.error("Select a template");

    // Demo dataset flow
    if (demoKey && (isSalesDemo || !file)) {
      setBusy(true);
      try {
        const r = await api.post(`/auditiq/demo-datasets/${demoKey}/seed`);
        toast.success(`Demo dataset seeded — ${r.data.dataset?.label || demoKey}`);
        nav(`/auditiq/dashboard?audit_id=${r.data.audit_id}`);
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Demo seed failed");
      }
      setBusy(false);
      return;
    }

    if (!file) return toast.error("Choose a CSV file");
    if (!file.name.toLowerCase().endsWith(".csv"))
      return toast.error("File must be a CSV");

    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const params = {
        template_key: templateKey,
        industry: industry,
      };
      // If user typed an org name, send it so backend auto-creates or links.
      if (orgName.trim()) params.organization_name = orgName.trim();
      const qs = new URLSearchParams(params);
      const r = await api.post(
        `/auditiq/upload?${qs.toString()}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" }, timeout: 180000 },
      );
      toast.success(`Audit complete — ${r.data.findings_created} findings`);
      nav(`/auditiq/dashboard?audit_id=${r.data.audit_id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    }
    setBusy(false);
  };

  const selectedIndustry = industries.find(i => i.key === industry);

  return (
    <PortalLayout>
      <div className="space-y-6">
        <div>
          <div className="text-[11px] uppercase tracking-[0.28em] text-electric">AuditIQ</div>
          <h1 className="font-display text-3xl md:text-4xl mt-2">Upload Audit Data</h1>
          <p className="text-sm text-cream-dim mt-1 max-w-3xl">
            Pick your industry, drop a CSV, and AuditIQ will auto-detect the best template,
            run the appropriate engines, and generate Chet's executive intelligence.
            An organization record is created automatically from your file.
          </p>
        </div>

        {/* Demo dataset picker */}
        {showDemoDropdown && (
          <div className="glass p-5" data-testid="demo-dataset-picker">
            <div className="text-[11px] uppercase tracking-[0.22em] text-gold mb-2">
              {isSalesDemo ? "Sales accounts use demo datasets only" : "Demo dataset (optional)"}
            </div>
            <Select value={demoKey || "__none"} onValueChange={(v) => onDemoDatasetChange(v === "__none" ? "" : v)}>
              <SelectTrigger className="bg-navy-deep border-gold-dim/40 text-cream" data-testid="demo-dataset-select">
                <SelectValue placeholder="Select a demo dataset…" />
              </SelectTrigger>
              <SelectContent className="bg-navy-soft border-gold-dim/40 text-cream max-h-80">
                <SelectItem value="__none">— none —</SelectItem>
                {demoDatasets.map(d => (
                  <SelectItem key={d.key} value={d.key}>{d.label} · {d.industry}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* STEP 1 — Industry */}
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-electric mb-2">Step 1 · Industry *</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-3"
               data-testid="industry-grid">
            {industries.map(ind => {
              const Icon = INDUSTRY_ICONS[ind.icon] || Building2;
              const active = ind.key === industry;
              return (
                <button
                  key={ind.key} type="button"
                  onClick={() => selectIndustry(ind.key)}
                  aria-pressed={active}
                  data-testid={`industry-card-${ind.key}`}
                  data-active={active ? "true" : "false"}
                  className={`text-left glass p-3 rounded-md border-2 transition-all ${
                    active
                      ? "border-gold ring-2 ring-gold/40 bg-gold/5"
                      : "border-transparent hover:border-electric/40"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <Icon size={18} className={active ? "text-gold" : "text-electric"} />
                    {ind.privacy_mode && <Lock size={12} className="text-amber-300" title="Privacy mode" />}
                  </div>
                  <div className={`text-sm font-medium mt-2 ${active ? "text-gold" : "text-cream"}`}>
                    {ind.display_name}
                  </div>
                  <div className="text-[11px] text-cream-dim mt-1 line-clamp-2">{ind.description}</div>
                  {active && (
                    <div className="text-[10px] text-gold mt-2 flex items-center gap-1">
                      <Check size={10} /> Selected
                    </div>
                  )}
                </button>
              );
            })}
          </div>
          {!industry && (
            <div className="mt-2 text-[11px] text-amber-300" data-testid="industry-required-hint">
              Select an industry to continue.
            </div>
          )}
        </div>

        {/* STEP 2+3 — Template, Org name, File */}
        <form onSubmit={submit} className="glass p-6 grid lg:grid-cols-2 gap-5 max-w-3xl">
          <div>
            <label className="text-[11px] uppercase tracking-[0.22em] text-steel">Template *</label>
            <Select value={templateKey} onValueChange={setTemplateKey} disabled={!industry}>
              <SelectTrigger className="mt-1 bg-navy-deep border-gold-dim/40 text-cream"
                             data-testid="auditiq-template-select">
                <SelectValue placeholder={industry ? "Select template" : "Select industry first"} />
              </SelectTrigger>
              <SelectContent className="bg-navy-soft border-gold-dim/40 text-cream">
                {templates.map(t => (
                  <SelectItem key={t.key} value={t.key}>
                    {t.recommended ? "★ " : ""}{t.display_name}{t.privacy_mode ? "  ·  Private" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedIndustry?.privacy_mode && (
              <div className="text-[10px] text-amber-300 mt-2 flex items-center gap-1">
                <Lock size={10} /> Industry-wide privacy mode — outputs are not publicly shareable.
              </div>
            )}
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-[0.22em] text-steel">Organization Name (optional)</label>
            <Input
              value={orgName} onChange={(e) => setOrgName(e.target.value)}
              placeholder="Auto-created from filename if blank"
              className="mt-1 bg-navy-deep border-gold-dim/40 text-cream"
              data-testid="auditiq-org-input"
            />
            <div className="text-[10px] text-cream-dim mt-1">
              We'll create an organization record automatically using this name.
            </div>
          </div>

          <div className="lg:col-span-2">
            {!isSalesDemo ? (
              <>
                <label className="text-[11px] uppercase tracking-[0.22em] text-steel">CSV file *</label>
                <Input type="file" accept=".csv,text/csv"
                  onChange={(e) => onFileChosen(e.target.files?.[0] || null)}
                  className="mt-1 bg-navy-deep border-gold-dim/40 text-cream"
                  data-testid="auditiq-file-input" />
              </>
            ) : (
              <div className="rounded-md border border-gold-dim/40 bg-navy-deep px-4 py-3 text-xs text-cream-dim"
                   data-testid="sales-demo-upload-note">
                File upload disabled for Sales accounts. Select a demo dataset above to generate a sample audit.
              </div>
            )}

            {detecting && (
              <div className="mt-3 text-[12px] text-cream-dim flex items-center gap-2" data-testid="detect-pending">
                <Loader2 size={12} className="animate-spin" /> Auto-detecting template…
              </div>
            )}
            {detection && !detecting && detection.auto_detected && detection.detected !== templateKey && (
              <div className="mt-3 glass p-3 rounded-md flex items-center justify-between gap-3 flex-wrap"
                   data-testid="detect-recommendation">
                <div className="text-sm">
                  <Sparkles size={12} className="text-electric inline mr-1" />
                  Recommended template: <span className="text-electric">{detection.display_name}</span>
                  <span className="text-cream-dim ml-2 text-xs">
                    ({Math.round((detection.confidence || 0) * 100)}% header match)
                  </span>
                </div>
                <Button type="button" onClick={useRecommended} className="btn-gold h-8 px-3 text-xs"
                        data-testid="detect-use-btn">
                  <Check size={12} className="mr-1" /> Use Recommended
                </Button>
              </div>
            )}
            {detection && !detecting && !detection.auto_detected && (
              <div className="mt-3 text-[12px] text-amber-300" data-testid="detect-low-confidence">
                Could not auto-detect template — please select manually.
              </div>
            )}
            {detection && !detecting && detection.detected === templateKey && (
              <div className="mt-3 text-[12px] text-emerald-300 flex items-center gap-1" data-testid="detect-match">
                <Check size={12} /> Template matches your file headers ({Math.round((detection.confidence || 0) * 100)}%).
              </div>
            )}
          </div>

          <div className="lg:col-span-2 flex justify-end">
            <Button type="submit" disabled={busy || !industry} className="btn-gold h-11 px-6"
                    data-testid="auditiq-upload-submit">
              {busy ? <><Loader2 size={14} className="mr-2 animate-spin" /> Running engine…</>
                    : <><Upload size={14} className="mr-2" /> Run Audit</>}
            </Button>
          </div>
        </form>
      </div>
    </PortalLayout>
  );
};

export default AuditUpload;
